import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageLayout, TableAction } from '@/components';
import { PermissionName } from '@/types';
import usePermission from '@/hooks/usePermission';
import useClaimData from '@/hooks/useClaimData';
import { ClaimListItem } from '@/types';
import { getClaimColumns } from './list';
import { ArrowUpDown, Tag, ChevronDown } from 'lucide-react';

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  'pending',
  'under-review',
  'compensated',
  'rejected',
  'closed',
] as const;

const TYPE_OPTIONS = ['damaged', 'partially_damaged', 'lost'] as const;

/* ─── Reusable filter dropdown (matches Orders/Finance pattern) ─── */
interface FilterDropdownProps {
  icon: React.ReactNode;
  label: string;
  value?: string;
  options: Array<{ key: string; label: string }>;
  onChange: (value?: string) => void;
  allLabel: string;
}

function FilterDropdown({ icon, label, value, options, onChange, allLabel }: FilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isActive = !!value;

  return (
    <div className='relative' ref={ref}>
      <button
        type='button'
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-solid text-[12px] font-semibold transition-colors shrink-0 cursor-pointer whitespace-nowrap ${
          isActive
            ? 'border-[rgb(237,224,74)] bg-[rgba(237,224,74,0.1)] text-[rgb(57,57,60)]'
            : 'border-[rgb(232,230,227)] bg-white text-[rgb(115,123,140)] hover:bg-[rgb(245,245,243)]'
        }`}
      >
        {icon}
        {value ? options.find((o) => o.key === value)?.label ?? label : label}
        <ChevronDown className='w-3.5 h-3.5' />
      </button>
      {open && (
        <div className='absolute top-full left-0 mt-1 z-50 bg-white rounded-xl border border-[rgb(232,230,227)] shadow-lg py-1.5 min-w-[180px]'>
          <button
            type='button'
            onClick={() => { onChange(undefined); setOpen(false); }}
            className={`w-full text-start px-3.5 py-2 text-[12px] cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors ${!value ? 'font-bold text-[rgb(57,57,60)]' : 'text-[rgb(115,123,140)]'}`}
          >
            {allLabel}
          </button>
          {options.map((opt) => (
            <button
              key={opt.key}
              type='button'
              onClick={() => { onChange(opt.key); setOpen(false); }}
              className={`w-full text-start px-3.5 py-2 text-[12px] cursor-pointer hover:bg-[rgb(245,245,243)] transition-colors ${value === opt.key ? 'font-bold text-[rgb(57,57,60)]' : 'text-[rgb(115,123,140)]'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const Claims = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();

  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);

  const { data, totals, isFetching } = useClaimData(
    currentPage,
    PAGE_SIZE,
    statusFilter,
    typeFilter,
  );

  const hasActiveFilter = !!statusFilter || !!typeFilter;

  const handleStatusChange = (value: string | undefined) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleTypeChange = (value: string | undefined) => {
    setTypeFilter(value);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setStatusFilter(undefined);
    setTypeFilter(undefined);
    setCurrentPage(1);
  };

  if (!hasPermission(PermissionName.VIEW_CLAIMS)) {
    return (
      <PageLayout>
        <div className='flex items-center justify-center py-16'>
          <span className='text-sm text-[rgb(115,123,140)]'>
            {String(t('no_permission', { defaultValue: 'No permission' }))}
          </span>
        </div>
      </PageLayout>
    );
  }

  const columns = getClaimColumns(t, navigate);

  const statusOptions = STATUS_OPTIONS.map((slug) => ({
    key: slug,
    label: String(t(`claim_status.${slug}`, { defaultValue: slug })),
  }));

  const typeOptions = TYPE_OPTIONS.map((type) => ({
    key: type,
    label: String(t(`claim_type.${type}`, { defaultValue: type })),
  }));

  return (
    <PageLayout>
      <div className='flex flex-col gap-4 w-full'>
        {/* Header + Filters in one row */}
        <div className='flex items-center gap-3 flex-wrap'>
          <h2 className='m-0 text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
            {String(t('claims', { defaultValue: 'Claims' }))}
          </h2>
          <div className='flex-1' />

          {/* Status filter pill */}
          <FilterDropdown
            icon={<ArrowUpDown className='w-3.5 h-3.5' />}
            label={String(t('status'))}
            value={statusFilter}
            options={statusOptions}
            onChange={handleStatusChange}
            allLabel={String(t('all_statuses', { defaultValue: 'All Statuses' }))}
          />

          {/* Type filter pill */}
          <FilterDropdown
            icon={<Tag className='w-3.5 h-3.5' />}
            label={String(t('type'))}
            value={typeFilter}
            options={typeOptions}
            onChange={handleTypeChange}
            allLabel={String(t('all_types', { defaultValue: 'All Types' }))}
          />

          {/* Reset link */}
          {hasActiveFilter && (
            <button
              type='button'
              onClick={handleResetFilters}
              className='text-[12px] text-[rgb(239,68,68)] font-medium cursor-pointer bg-transparent border-none p-0 hover:underline'
            >
              {String(t('reset_filters', { defaultValue: 'Reset' }))}
            </button>
          )}
        </div>

        {isFetching && data.length === 0 ? (
          <div className='flex justify-center py-16'>
            <div className='h-8 w-8 rounded-full border-2 border-[rgb(237,224,74)] border-t-transparent animate-spin' />
          </div>
        ) : data.length === 0 ? (
          <div className='flex flex-col items-center justify-center gap-2 py-16'>
            <span className='text-sm font-medium text-[rgb(57,57,60)]'>
              {String(t('no_claims_yet', { defaultValue: 'No claims yet' }))}
            </span>
            <span className='text-sm text-[rgb(115,123,140)]'>
              {String(
                t('no_claims_description', {
                  defaultValue: 'Claims filed on your orders will appear here.',
                }),
              )}
            </span>
          </div>
        ) : (
          <TableAction<ClaimListItem>
            columns={columns}
            data={data}
            pageSize={PAGE_SIZE}
            current={currentPage}
            total={totals}
            loading={isFetching}
            onPaginationChange={(page) => setCurrentPage(page)}
            rowSelectionEnabled={false}
            onRow={(record) => ({
              onClick: () => navigate(`/claims/${record.uid}`),
              className: 'cursor-pointer',
            })}
          />
        )}
      </div>
    </PageLayout>
  );
};

export default Claims;
